import React, { useEffect, useState } from "react";
import { type CredentialResponse } from "@react-oauth/google";
import { Link, useNavigate } from "react-router-dom";
import { createUser } from "@/services/authService";
import { useAuth } from "@/context/AuthContext";
import AuthLayout from "@/components/auth/AuthLayout";
import AuthField from "@/components/auth/AuthField";
import AuthGoogleButton from "@/components/auth/AuthGoogleButton";
import { Checkbox } from "@/components/vb/Checkbox";
import AuthAlert from "@/components/auth/AuthAlert";
import { AuthMiniQuote } from "@/components/auth/AuthProof";
import {
    getEmailError,
    SUPPORT_EMAIL,
    MIN_PASSWORD_LENGTH,
    PASSWORD_RULE,
} from "@/components/auth/authCopy";

const TERMS_ERROR_ID = "auth-terms-error";

type FieldErrors = {
    name?: string | null;
    lastName?: string | null;
    email?: string | null;
    password?: string | null;
    terms?: string | null;
};

export default function Register() {
    const { isAuthenticated, signInWithGoogle, signIn } = useAuth();
    const navigate = useNavigate();

    const [form, setForm] = useState({
        name: "",
        lastName: "",
        email: "",
        password: "",
    });

    const [agreeTerms, setAgreeTerms] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isAuthenticated) {
            navigate("/");
        }
    }, [isAuthenticated, navigate]);

    const handleChange =
        (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
            setForm((prev) => ({ ...prev, [field]: e.target.value }));
            setFieldErrors((prev) => ({ ...prev, [field]: null }));
        };

    const handleRegistration = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError("");

        const { name, lastName, email, password } = form;

        const next: FieldErrors = {
            name: name.trim() ? null : "Type your first name.",
            lastName: lastName.trim() ? null : "Type your last name.",
            email: getEmailError(email),
            password:
                password.length < MIN_PASSWORD_LENGTH
                    ? `Your password needs at least ${MIN_PASSWORD_LENGTH} characters.`
                    : null,
            terms: agreeTerms ? null : "Tick the box to agree to the Terms first.",
        };
        setFieldErrors(next);
        if (Object.values(next).some(Boolean)) return;

        try {
            setLoading(true);
            const response = await createUser(name.trim(), lastName.trim(), email.trim(), password);
            if (response.status !== 201) {
                setError("Failed to create user. Please try again.");
                return;
            }
            await signIn(email.trim(), password);
            navigate("/", { replace: true });
            setAgreeTerms(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : "An unexpected error occurred.");
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSuccess = async (res: CredentialResponse) => {
        if (!res.credential) return;
        try {
            setLoading(true);
            await signInWithGoogle(res.credential);
            navigate("/", { replace: true });
        } catch {
            setError("Google sign-in failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    if (isAuthenticated) return null;

    return (
        <AuthLayout page="create" title="Create your account.">
            <AuthGoogleButton
                text="signup_with"
                onSuccess={handleGoogleSuccess}
                onError={() => setError("Google sign-in failed. Please try again.")}
            />

            <div className="auth-or" aria-hidden="true">
                OR
            </div>

            <form onSubmit={handleRegistration} noValidate>
                <div className="auth-name-row">
                    <AuthField
                        label="First name"
                        name="first_name"
                        autoComplete="given-name"
                        placeholder="Ray"
                        mobilePlaceholder="First name"
                        value={form.name}
                        error={fieldErrors.name}
                        onChange={handleChange("name")}
                    />
                    <AuthField
                        label="Last name"
                        name="last_name"
                        autoComplete="family-name"
                        placeholder="Mendez"
                        mobilePlaceholder="Last name"
                        value={form.lastName}
                        error={fieldErrors.lastName}
                        onChange={handleChange("lastName")}
                    />
                </div>

                <AuthField
                    fieldClassName="auth-field--email"
                    label="Email"
                    type="email"
                    name="email"
                    autoComplete="email"
                    placeholder="you@yourcompany.com"
                    mobilePlaceholder="Email"
                    value={form.email}
                    error={fieldErrors.email}
                    onChange={handleChange("email")}
                    onBlur={() => {
                        if (form.email.trim()) {
                            setFieldErrors((prev) => ({ ...prev, email: getEmailError(form.email) }));
                        }
                    }}
                />

                <AuthField
                    fieldClassName="auth-field--password"
                    label="Create a password"
                    type="password"
                    name="password"
                    autoComplete="new-password"
                    placeholder="Create a password"
                    mobilePlaceholder="Create a password"
                    reveal
                    hint={PASSWORD_RULE}
                    value={form.password}
                    error={fieldErrors.password}
                    onChange={handleChange("password")}
                />

                <Checkbox
                    className="auth-terms"
                    aria-describedby={fieldErrors.terms ? TERMS_ERROR_ID : undefined}
                    aria-invalid={fieldErrors.terms ? true : undefined}
                    checked={agreeTerms}
                    onChange={(e) => {
                        setAgreeTerms(e.target.checked);
                        if (e.target.checked) {
                            setFieldErrors((prev) => ({ ...prev, terms: null }));
                        }
                    }}
                >
                    I agree to the{" "}
                    <a
                        href={`${import.meta.env.VITE_LANDING_URL}/terms`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Terms
                    </a>{" "}
                    and{" "}
                    <a
                        href={`${import.meta.env.VITE_LANDING_URL}/privacy`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Privacy Policy
                    </a>
                    .
                </Checkbox>
                <span
                    className={
                        fieldErrors.terms ? "auth-error auth-error--show" : "auth-error"
                    }
                    id={TERMS_ERROR_ID}
                    role="alert"
                >
                    {fieldErrors.terms ?? ""}
                </span>

                <p className="auth-note">
                    Your trial starts after you pick a plan. Your card comes then, not now.
                </p>

                {error ? <AuthAlert>{error}</AuthAlert> : null}

                <button className="auth-btn-primary" type="submit" disabled={loading}>
                    {loading ? "Creating…" : "Create my account"}
                </button>
            </form>

            <AuthMiniQuote />

            <p className="auth-alt auth-alt--center-m">
                Already have an account? <Link to="/login">Sign in</Link>
            </p>

            <div className="auth-help auth-help--single d-only">
                Questions before you start? Email{" "}
                <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
            </div>
        </AuthLayout>
    );
}
